import { useState } from 'react';

// ---------------------------------------------------------------------------
// QuickStart — optional AI fast-path at the start of the prospect intake.
// Two paths, both skippable:
//   1. Describe your business  → classify → confirm → set industry (supplement)
//   2. Paste your website URL  → enrich   → review  → prefill base fields
// If AI is unavailable it falls straight through to the manual flow.
// ---------------------------------------------------------------------------

interface ClassifyAlternative {
  industry_category: string;
  industry_subcategory: string;
  label: string;
  supplement: string | null;
}

interface ClassifyResponse {
  industry_category: string;
  industry_subcategory: string;
  label: string;
  supplement: string | null;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  alternatives: ClassifyAlternative[];
}

interface IndustryHint {
  category: string;
  subcategory: string;
  label: string;
  supplement: string | null;
}

interface EnrichResponse {
  fields: Record<string, string | number | string[]>;
  industry_hint: IndustryHint | null;
  confidence: 'high' | 'medium' | 'low';
  warnings: string[];
}

export interface AppliedClassification {
  /** Supplement key — sets the engine's industry_type answer to load the supplement. */
  industry_type: string | null;
  industry_category: string;
  industry_subcategory: string;
  label: string;
}

export interface AppliedEnrichment {
  /** Engine field_path → value, to prefill question answers. */
  fields: Record<string, string | number | string[]>;
  industry_type: string | null;
  industry_category: string | null;
  industry_subcategory: string | null;
}

interface QuickStartProps {
  onApply: (classification: AppliedClassification) => void;
  onApplyEnrichment: (enrichment: AppliedEnrichment) => void;
  onSkip: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  'business.legal_name': 'Business name',
  'business.description': 'What you do',
  'business.address.street': 'Street',
  'business.address.city': 'City',
  'business.address.state': 'State',
  'business.address.zip': 'ZIP',
  'contact.phone': 'Phone',
  'business.years_in_business': 'Years in business',
  'business.services': 'Services',
};

function displayValue(v: string | number | string[]): string {
  return Array.isArray(v) ? v.join(', ') : String(v);
}

type Stage = 'input' | 'loading' | 'classifyResult' | 'enrichReview';

export function QuickStart({ onApply, onApplyEnrichment, onSkip }: QuickStartProps) {
  const [stage, setStage] = useState<Stage>('input');
  const [loadingMsg, setLoadingMsg] = useState('Analyzing…');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');
  const [result, setResult] = useState<ClassifyResponse | null>(null);
  const [enrich, setEnrich] = useState<EnrichResponse | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  async function classify() {
    if (description.trim().length < 3) {
      setError('Tell us a little about your business first.');
      return;
    }
    setError(undefined);
    setLoadingMsg('Analyzing your business…');
    setStage('loading');
    try {
      const res = await fetch('/api/intake/classify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description }),
      });
      if (!res.ok) throw new Error('unavailable');
      setResult((await res.json()) as ClassifyResponse);
      setStage('classifyResult');
    } catch {
      onSkip();
    }
  }

  async function enrichWebsite() {
    if (website.trim().length < 4) {
      setError('Enter your website address first.');
      return;
    }
    setError(undefined);
    setLoadingMsg('Reading your website…');
    setStage('loading');
    try {
      const res = await fetch('/api/intake/enrich-website', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: website }),
      });
      if (res.status === 503) {
        onSkip();
        return;
      }
      if (!res.ok) {
        setError("We couldn't read that website. Check the address or fill things in manually.");
        setStage('input');
        return;
      }
      setEnrich((await res.json()) as EnrichResponse);
      setStage('enrichReview');
    } catch {
      setError("We couldn't reach that website. Try again or fill things in manually.");
      setStage('input');
    }
  }

  async function extractPdf(file: File) {
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF.');
      return;
    }
    setError(undefined);
    setLoadingMsg('Reading your document…');
    setStage('loading');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/intake/extract-document', { method: 'POST', body: form });
      if (res.status === 503) {
        onSkip();
        return;
      }
      if (!res.ok) {
        setError("We couldn't read that document. Try another file or fill things in manually.");
        setStage('input');
        return;
      }
      // Response shape matches the website-enrichment review.
      setEnrich((await res.json()) as EnrichResponse);
      setStage('enrichReview');
    } catch {
      setError("We couldn't process that document. Fill things in manually instead.");
      setStage('input');
    }
  }

  function chooseClassification(c: ClassifyResponse | ClassifyAlternative) {
    onApply({
      industry_type: c.supplement ?? null,
      industry_category: c.industry_category,
      industry_subcategory: c.industry_subcategory,
      label: c.label,
    });
  }

  function useEnrichment() {
    if (!enrich) return;
    onApplyEnrichment({
      fields: enrich.fields,
      industry_type: enrich.industry_hint?.supplement ?? null,
      industry_category: enrich.industry_hint?.category ?? null,
      industry_subcategory: enrich.industry_hint?.subcategory ?? null,
    });
  }

  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 md:p-10 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {stage === 'input' && (
        <>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Let's get started</h2>
          <p className="text-slate-600 mb-6">
            Give us a head start and we'll set up the right questions and pre-fill what we can. Or
            skip and fill everything in yourself.
          </p>

          <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="qs-desc">
            Describe your business in a sentence
          </label>
          <textarea
            id="qs-desc"
            value={description}
            onChange={(e) => {
              setError(undefined);
              setDescription(e.target.value);
            }}
            placeholder="e.g. We're a residential plumbing company in St. Louis doing repairs and remodels."
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none resize-none"
          />
          <button
            type="button"
            onClick={classify}
            className="mt-3 w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 font-medium transition shadow-sm"
          >
            Continue →
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-sm text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="qs-url">
            Paste your website and we'll read it for you
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              id="qs-url"
              type="url"
              value={website}
              onChange={(e) => {
                setError(undefined);
                setWebsite(e.target.value);
              }}
              placeholder="yourcompany.com"
              className="flex-1 rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
            />
            <button
              type="button"
              onClick={enrichWebsite}
              className="w-full sm:w-auto rounded-lg border border-blue-600 text-blue-700 hover:bg-blue-50 px-5 py-3 font-medium transition whitespace-nowrap"
            >
              Use my site
            </button>
          </div>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-sm text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <span className="block text-sm font-medium text-slate-700 mb-2">
            Upload an existing application or policy
          </span>
          <label className="flex items-center justify-center gap-2 w-full rounded-lg border border-dashed border-slate-300 px-4 py-3 text-slate-600 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer transition">
            <svg
              aria-hidden="true"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span className="font-medium">Choose a PDF</span>
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) extractPdf(f);
              }}
            />
          </label>

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onSkip}
              className="text-slate-500 hover:text-slate-700 text-sm font-medium"
            >
              Skip — I'll fill it in myself
            </button>
          </div>
        </>
      )}

      {stage === 'loading' && (
        <div className="py-10 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
          <p className="mt-4 text-slate-600">{loadingMsg}</p>
        </div>
      )}

      {stage === 'classifyResult' && result && (
        <>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Here's what we found</h2>
          <div className="rounded-xl bg-blue-50/60 border border-blue-100 p-5 my-4">
            <div className="text-sm text-slate-500">Looks like</div>
            <div className="text-xl font-semibold text-slate-900">{result.label}</div>
            <p className="mt-2 text-sm text-slate-600">{result.reasoning}</p>
          </div>

          <button
            type="button"
            onClick={() => chooseClassification(result)}
            className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 font-medium transition shadow-sm"
          >
            Yes, that's right →
          </button>

          {result.alternatives.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-medium text-slate-500 mb-2">Or pick a closer match:</div>
              <div className="flex flex-wrap gap-2">
                {result.alternatives.map((alt) => (
                  <button
                    type="button"
                    key={`${alt.industry_category}/${alt.industry_subcategory}`}
                    onClick={() => chooseClassification(alt)}
                    className="rounded-full border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:border-blue-400 hover:bg-blue-50 transition"
                  >
                    {alt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={() => setStage('input')}
              className="text-slate-600 hover:text-slate-900 text-sm font-medium"
            >
              ← Try again
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="text-slate-500 hover:text-slate-700 text-sm"
            >
              None of these — I'll fill it in
            </button>
          </div>
        </>
      )}

      {stage === 'enrichReview' && enrich && (
        <>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Here's what we found</h2>
          <p className="text-slate-600 mb-4">
            We'll fill these in for you and only ask about what's still missing.
          </p>

          {enrich.industry_hint && (
            <div className="rounded-xl bg-blue-50/60 border border-blue-100 p-4 mb-4">
              <span className="text-sm text-slate-500">Industry: </span>
              <span className="font-semibold text-slate-900">{enrich.industry_hint.label}</span>
            </div>
          )}

          <dl className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
            {Object.entries(enrich.fields).map(([path, value]) => (
              <div key={path} className="flex gap-4 px-4 py-3">
                <dt className="w-36 shrink-0 text-sm text-slate-500">
                  {FIELD_LABELS[path] ?? path}
                </dt>
                <dd className="text-sm text-slate-900">{displayValue(value)}</dd>
              </div>
            ))}
          </dl>

          {enrich.warnings.map((w) => (
            <p key={w} className="mt-3 text-sm text-amber-600">
              {w}
            </p>
          ))}

          <button
            type="button"
            onClick={useEnrichment}
            className="mt-5 w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 font-medium transition shadow-sm"
          >
            Use these answers →
          </button>

          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={() => setStage('input')}
              className="text-slate-600 hover:text-slate-900 text-sm font-medium"
            >
              ← Start over
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="text-slate-500 hover:text-slate-700 text-sm"
            >
              I'll fill it in myself
            </button>
          </div>
        </>
      )}
    </div>
  );
}
